import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Cake, Heart, Frown, ArrowRight, Star, RefreshCw, CheckCircle2 } from 'lucide-react'
import styles from './CSMDashboard.module.css'
import AuthContext from '../../store/auth-context'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import { smPost } from './smApi'
import { prettifyOccasionType, emojiForIcon } from './smIcons'
import SMSetupState from './SMSetupState'
import SMCoverageNudge from './SMCoverageNudge'

// Backend resource name has no leading slash — BASE_URL_API already ends in '/'.
const SM_GET_DASHBOARD_SUMMARY = 'sm_get_dashboard_summary'

// Shown only on the very first visit (see hasOccasionData below) — the
// zero state has the whole page to itself, so this is the one place it's
// worth spelling out what the feature actually does in plain, three-step terms.
const HOW_IT_WORKS_STEPS = [
    {
        title: 'Add a date',
        desc: "Enter a client's birthday or anniversary once — that's the only manual step.",
    },
    {
        title: 'We track it',
        desc: 'It shows up right here every morning, automatically. Nothing to remember.',
    },
    {
        title: 'You send a wish',
        desc: 'One tap sends a warm WhatsApp or SMS greeting — no typing needed.',
    },
]

const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    return 'Good Evening'
}

const CSMDashboard = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const displayName = useMemo(() => {
        try {
            return JSON.parse(authCtx?.loginData)?.welcomeData?.displayName || 'there'
        } catch {
            return 'there'
        }
    }, [authCtx?.loginData])

    const [summary, setSummary] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadErrorMessage, setLoadErrorMessage] = useState(null)

    const loadSummary = useCallback(async () => {
        setLoading(true)
        setLoadErrorMessage(null)
        const res = await smPost(authCtx, activeStore, SM_GET_DASHBOARD_SUMMARY)
        if (res?.status === 'success' && res?.response) {
            setSummary(res.response)
        } else {
            setLoadErrorMessage(res?.message || "Couldn't load Special Moments right now.")
        }
        setLoading(false)
    }, [authCtx, activeStore])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadSummary()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading today's celebrations…</span>
            </div>
        )
    }

    if (loadErrorMessage || !summary) {
        return (
            <div className={styles.loadingWrap}>
                <span>{loadErrorMessage || "Couldn't load Special Moments right now."}</span>
                <button type="button" className={styles.retryBtn} onClick={loadSummary}>Retry</button>
            </div>
        )
    }

    const {
        today, week, month,
        recently_missed_count: recentlyMissedCount,
        todays_preview: todaysPreview = [],
        has_occasion_data: hasOccasionData,
        setup_message: setupMessage,
        clients_with_occasion: clientsWithOccasion,
        total_clients: totalClients,
    } = summary

    if (hasOccasionData === false) {
        return (
            <div className={styles.page}>
                <SMSetupState message={setupMessage} />

                <div className={styles.howItWorks}>
                    <div className={styles.howItWorksLabel}>How It Works</div>
                    <div className={styles.stepsRow}>
                        {HOW_IT_WORKS_STEPS.map((step, i) => (
                            <div key={step.title} className={styles.step}>
                                <div className={styles.stepNumber}>{i + 1}</div>
                                <div className={styles.stepTitle}>{step.title}</div>
                                <p className={styles.stepDesc}>{step.desc}</p>
                            </div>
                        ))}
                    </div>
                    <p className={styles.howItWorksClosing}>
                        People remember who remembers them —{' '}
                        <span className={styles.closingAccent}>and they come back.</span>
                    </p>
                </div>
            </div>
        )
    }

    const showCoverageNudge = totalClients > 0 && clientsWithOccasion < totalClients

    return (
        <div className={styles.page}>
            {showCoverageNudge && (
                <SMCoverageNudge clientsWithOccasion={clientsWithOccasion} totalClients={totalClients} />
            )}

            {/* Today's Celebration Banner — the warm micro-feature */}
            <div className={styles.banner}>
                <div className={styles.bannerText}>
                    <div className={styles.bannerTitle}>🎉 Today We Celebrate</div>
                    <div className={styles.bannerCounts}>
                        <span>{today.birthdays} Birthdays</span>
                        <span className={styles.bannerDot}>•</span>
                        <span>{today.anniversaries} Anniversaries</span>
                    </div>
                    <div className={styles.bannerTagline}>Let's make someone's day special ❤️</div>
                </div>
            </div>

            <div className={styles.greeting}>{getGreeting()}, {displayName} 👋</div>
            <p className={styles.subheading}>
                Today there are <strong>{today.birthdays} Birthdays</strong> and{' '}
                <strong>{today.anniversaries} Anniversaries</strong> — {today.total} customers to celebrate today.
            </p>

            {/* Summary cards: Today / This Week / This Month */}
            <div className={styles.summaryGrid}>
                <Link to="/special-moments/inbox" className={styles.summaryCard}>
                    <div className={styles.summaryCardLabel}>Today</div>
                    <div className={styles.summaryCardRow}>
                        <span className={styles.summaryPill}><Cake size={14} />{today.birthdays} Birthdays</span>
                        <span className={styles.summaryPill}><Heart size={14} />{today.anniversaries} Anniversaries</span>
                    </div>
                    <div className={styles.summaryTotal}>{today.total} Customers to Celebrate Today</div>
                </Link>

                <Link to="/special-moments/calendar" className={styles.summaryCard}>
                    <div className={styles.summaryCardLabel}>This Week</div>
                    <div className={styles.summaryCardRow}>
                        <span className={styles.summaryPill}><Cake size={14} />{week.birthdays} Birthdays</span>
                        <span className={styles.summaryPill}><Heart size={14} />{week.anniversaries} Anniversaries</span>
                    </div>
                    <div className={styles.summaryTotal}>{week.total} Celebrations</div>
                </Link>

                <Link to="/special-moments/calendar" className={styles.summaryCard}>
                    <div className={styles.summaryCardLabel}>This Month</div>
                    <div className={styles.summaryCardRow}>
                        <span className={styles.summaryPill}><Cake size={14} />{month.birthdays} Birthdays</span>
                        <span className={styles.summaryPill}><Heart size={14} />{month.anniversaries} Anniversaries</span>
                    </div>
                    <div className={styles.summaryTotal}>{month.total} Celebrations</div>
                </Link>
            </div>

            {/* Recently Missed */}
            {recentlyMissedCount > 0 ? (
                <Link to="/special-moments/inbox" className={styles.missedCard}>
                    <div className={styles.missedIconWrap}>
                        <Frown size={18} />
                    </div>
                    <div className={styles.missedText}>
                        <div className={styles.missedTitle}>Recently Missed</div>
                        <div className={styles.missedSubtitle}>{recentlyMissedCount} Customers</div>
                    </div>
                    <span className={styles.missedCta}>
                        Wish Them Now <ArrowRight size={14} />
                    </span>
                </Link>
            ) : (
                <div className={`${styles.missedCard} ${styles.missedCardClear}`}>
                    <div className={`${styles.missedIconWrap} ${styles.missedIconWrapClear}`}>
                        <CheckCircle2 size={18} />
                    </div>
                    <div className={styles.missedText}>
                        <div className={styles.missedTitle}>Recently Missed</div>
                        <div className={styles.missedSubtitleClear}>All caught up — 0 Customers</div>
                    </div>
                </div>
            )}

            {/* Today's Celebrations preview */}
            <div className={styles.sectionHeader}>
                <span className={styles.sectionLabel}>Today's Celebrations</span>
                {todaysPreview.length > 0 && (
                    <Link to="/special-moments/inbox" className={styles.viewAllLink}>
                        View All <ArrowRight size={13} />
                    </Link>
                )}
            </div>

            <div className={styles.previewList}>
                {todaysPreview.map((c) => {
                    const label = prettifyOccasionType(c.occasion_type)
                    return (
                        <Link
                            key={c.client_store_relation_identifier}
                            to={`/special-moments/customer/${c.client_store_relation_identifier}`}
                            className={styles.previewCard}
                        >
                            <span className={styles.previewEmoji}>{emojiForIcon(c.icon)}</span>
                            <div className={styles.previewText}>
                                <div className={styles.previewName}>
                                    {c.name}
                                    {c.is_vip && (
                                        <span className={styles.vipBadge}>
                                            <Star size={11} fill="currentColor" /> VIP
                                        </span>
                                    )}
                                </div>
                                <div className={styles.previewOccasion}>
                                    {label}{label.includes('Birthday') ? ' Today' : ''}
                                </div>
                            </div>
                        </Link>
                    )
                })}
                {todaysPreview.length === 0 && (
                    <div className={styles.emptyPreview}>No celebrations to preview today.</div>
                )}
            </div>
        </div>
    )
}

export default CSMDashboard
